import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import _ from 'lodash';

import {BucketedQueryState} from './bucketedQueryManager';

export type BucketedRunsReducerState = {
  aggregatedByRunsetId: Record<string, boolean>;
  loadingByRunSetId: Record<string, boolean>;
  isLoading: boolean;
  runSetsByRunsetId: Record<string, boolean>;
  runDataByRunsetId: Record<string, BucketedQueryState>;
  runNames: string[];
  totalRuns: number;
};
type LoadingAction = {
  type: 'bucketedRuns/setLoadingByRunSetId';
  runsetId: string;
  loading: boolean;
};
type ComboAction = {
  type: 'bucketedRuns/combo';
  data: BucketedQueryState;
  isAggregated: boolean;
  loading: boolean;
  runsetId: string;
  totalRuns?: number;
};
type RunDataAction = {
  type: 'bucketedRuns/setRunDataByRunsetId';
  runsetId: string;
  data: BucketedQueryState;
};
type TotalRunsAction = {
  type: 'bucketedRuns/setTotalRuns';
  totalRuns?: number;
};
type AggregatedAction = {
  type: 'bucketedRuns/setAggregatedByRunsetId';
  runsetId: string;
  isAggregated: boolean;
};
type BucketedRunsReducerAction =
  | LoadingAction
  | AggregatedAction
  | {
      type: 'bucketedRuns/setRunSetEnabled';
      enabledStatusByRunsetId: Record<string, boolean>;
    }
  | RunDataAction
  | TotalRunsAction
  | ComboAction;

function handleSetLoadingByRunSetId(
  state: BucketedRunsReducerState,
  action: ComboAction | LoadingAction
) {
  const oldState = state.loadingByRunSetId[action.runsetId];
  if (Boolean(oldState) === Boolean(action.loading)) {
    return state;
  }

  const newLoadingByRunSetId = {
    ...state.loadingByRunSetId,
    [action.runsetId]: action.loading,
  };
  return {
    ...state,
    loadingByRunSetId: newLoadingByRunSetId,
    isLoading: Object.values(newLoadingByRunSetId).some(Boolean),
  };
}

function handleSetTotalRuns(
  state: BucketedRunsReducerState,
  action: ComboAction | TotalRunsAction
) {
  if (state.totalRuns === action.totalRuns) {
    return state;
  }

  return {
    ...state,
    totalRuns: action.totalRuns ?? 0,
  };
}

function handleSetRunNames(state: BucketedRunsReducerState) {
  const updateNames = Object.values(state.runDataByRunsetId)
    .flatMap(({data}) => Object.values(data?.runsById ?? {}).map(r => r.name))
    .filter(isNotNullOrUndefined);

  if (!_.isEqual(state.runNames, updateNames)) {
    return {
      ...state,
      runNames: updateNames,
    };
  }

  return state;
}

function handleSetRunDataByRunsetId(
  state: BucketedRunsReducerState,
  action: ComboAction | RunDataAction
) {
  // this deep equals check is much, much cheaper than an extra render,
  // and much less costly than missing a subtle difference with a hand-rolled equality check,
  // so it's worth the price
  const hasChanged = !_.isEqual(
    state.runDataByRunsetId[action.runsetId]?.data?.runsById,
    action.data?.data?.runsById
  );

  if (hasChanged) {
    const updatedState = {
      ...state,
      runDataByRunsetId: {
        ...state.runDataByRunsetId,
        [action.runsetId]: action.data ?? {},
      },
    };
    return handleSetRunNames(updatedState);
  }
  return state;
}

function handleSetAggregatedByRunsetId(
  state: BucketedRunsReducerState,
  action: ComboAction | AggregatedAction
) {
  /**
   * To avoid unnecessary renders, we only want to update the isAggregated state when the new state is different from the old state when both states are coerced to booleans:
   * 1. old state: undefined, new state: false => no update
   * 2. old state: false, new state: false => no update
   * 3. old state: false, new state: true => update
   * 4. old state: true, new state: false => update
   * 5. old state: true, new state: true => no update
   * 6. old state: true, new state: undefined => update
   * etc...
   */

  const prevValue = !!state.aggregatedByRunsetId[action.runsetId];
  const newValue = !!action.isAggregated;
  if (prevValue !== newValue) {
    return {
      ...state,
      aggregatedByRunsetId: {
        ...state.aggregatedByRunsetId,
        [action.runsetId]: action.isAggregated,
      },
    };
  }
  return state;
}

export function bucketedRunsReducer(
  state: BucketedRunsReducerState,
  action: BucketedRunsReducerAction
) {
  switch (action.type) {
    case 'bucketedRuns/combo': {
      // the combo action is a bunch of discrete state transformations that we want to do in a single pass
      // this keeps the state from thrashing if multiple changes need to be batched so that we don't flicker
      // the UI. Writing it this way allowed me to keep each state transformation separate
      return [
        handleSetAggregatedByRunsetId,
        handleSetLoadingByRunSetId,
        handleSetTotalRuns,
        handleSetRunDataByRunsetId,
      ].reduce((acc, handler) => {
        return handler(acc, action);
      }, state);
    }

    case 'bucketedRuns/setAggregatedByRunsetId': {
      return handleSetAggregatedByRunsetId(state, action);
    }

    case 'bucketedRuns/setLoadingByRunSetId': {
      return handleSetLoadingByRunSetId(state, action);
    }

    // This is a critical reducer computation: making sure we keep reference equality here when polling updates but no data changes is critical for making sure the UI doesn't thrash and try to render all the panels. The output state here is what ultimately gets serialized down into the cache as well.
    case 'bucketedRuns/setRunDataByRunsetId': {
      return handleSetRunDataByRunsetId(state, action);
    }

    case 'bucketedRuns/setRunSetEnabled': {
      const hasChanged = !_.isEqual(
        state.runSetsByRunsetId,
        action.enabledStatusByRunsetId
      );
      if (hasChanged) {
        return {
          ...state,
          runSetsByRunsetId: action.enabledStatusByRunsetId,
        };
      }

      return state;
    }

    case 'bucketedRuns/setTotalRuns': {
      return handleSetTotalRuns(state, action);
    }

    default: {
      return state;
    }
  }
}
